import { apiErrorMessage } from '@agos/api-client'
import type { Vehicle } from '@agos/api-client/model'
import {
  getGetVehiclesQueryKey,
  useDeleteVehiclesVehicleId,
  useGetVehicles,
  usePatchVehiclesVehicleId,
  usePostVehicles,
} from '@agos/api-client/orders'
import { formatCents, formatDayMonth, formatDecimal, T, tt } from '@agos/shared'
import {
  BUTTON_GHOST,
  BUTTON_PRIMARY,
  cn,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  ReadFailedBlock,
  StatusBadge,
  toast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Car, Fuel, Gauge, Pencil, Plus, Snowflake, Sun, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  EXPIRY_CLASSES,
  FUEL_TYPE_CLASSES,
  expiryStatus,
  fleetWarnings,
  fuelCostCents,
  litresUsed,
  odometerReading,
  worstExpiry,
} from './fleet-math'
import { VehicleModal } from './VehicleModal'

/**
 * `/vehicle-fleet` — the cars, ported from legacy `pages/vehicle-fleet.tsx`.
 *
 * The screen exists for two questions: what is each car costing in fuel, and
 * whose paperwork is about to lapse. The second is the one with consequences —
 * driving on an expired STK is an offence and this page is the only place the
 * date is shown — so the expiry bands are their own tested module.
 */

/**
 * FB-816, §9.537 - drawn instead of the fleet, never beside it.
 *
 * The warning line above is behind `expired > 0 || soon > 0`, so on a failed read it is simply **absent** -
 * and on this screen an absent warning reads as *nothing is out of date*. Šurany's vehicle 1 is active with
 * `stk_expiry = 2025-09-19` and `insurance_expiry = 2026-01-14`, so the red line is rendering on their data
 * today; the failure mode is a live warning replaced by silence, not a hypothetical empty fleet.
 *
 * Legacy guarded this and the port dropped it - `vehicle-fleet.tsx:654` draws the message with a retry and
 * gates both its empty state and its grid on `!error`. Its hook returns `error` among five values, so the
 * guard is visible at every use; `useQuery`'s `isError` has to be asked for.
 */
function VehicleFleetLoadFailed({ retry }: { retry: () => void }) {
  return (
    <ReadFailedBlock
      marker="fleet"
      title={tt('Vozový park sa nepodarilo načítať')}
      icon={<AlertTriangle className="w-4 h-4" />}
      onRetry={retry}
    >
      <T>
        Preto sa nezobrazuje ani upozornenie na doklady po platnosti - nevieme, či nejaké sú. Skúste to
        znova.
      </T>
    </ReadFailedBlock>
  )
}

const FUEL_TYPE_LABELS: Record<string, string> = {
  benzin: tt('Benzín'),
  nafta: tt('Nafta'),
  lpg_benzin: tt('LPG/Benzín'),
  electric: tt('Elektromobil'),
}

const KM_FORMAT = new Intl.NumberFormat('sk-SK')

export function VehicleFleetPage() {
  const queryClient = useQueryClient()
  const [now] = useState(() => Date.now())
  const [editing, setEditing] = useState<Vehicle | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<Vehicle | null>(null)

  const { data, isLoading, isError, refetch } = useGetVehicles({})
  const createVehicle = usePostVehicles()
  const updateVehicle = usePatchVehiclesVehicleId()
  const deleteVehicle = useDeleteVehiclesVehicleId()

  const vehicles = useMemo(() => (Array.isArray(data) ? data : []), [data])

  /**
   * Worst paperwork first. A manager opening this page needs the car that
   * cannot legally leave the yard at the top, not the alphabetical first.
   */
  const sorted = useMemo(() => {
    const rank: Record<string, number> = { expired: 0, soon: 1, none: 2, valid: 3 }
    return [...vehicles].sort(
      (left, right) =>
        (rank[worstExpiry(left, now)] ?? 3) - (rank[worstExpiry(right, now)] ?? 3) ||
        (left.name ?? '').localeCompare(right.name ?? '', 'sk'),
    )
  }, [vehicles, now])

  /**
   * The line the owner asked for — §9.205. The nightly check already raises a notification per
   * document (§10A.72) and that stays; this is for somebody standing on this page, for whom the
   * same facts were spread across four colours on n cards.
   */
  const warnings = useMemo(() => fleetWarnings(vehicles, now), [vehicles, now])

  const refresh = () => void queryClient.invalidateQueries({ queryKey: getGetVehiclesQueryKey() })

  const save = async (form: Record<string, unknown>) => {
    const onError = (caught: unknown) =>
      toast({
        variant: 'destructive',
        title: tt('Uloženie zlyhalo'),
        description: apiErrorMessage(caught, ''),
      })

    if (editing?.id != null) {
      await updateVehicle.mutateAsync({ vehicleId: editing.id, data: form }).catch(onError)
    } else {
      await createVehicle.mutateAsync({ data: form }).catch(onError)
    }
    refresh()
    setEditing(null)
    setIsCreating(false)
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={tt('Vozový park')}
        description={tt('Náklady na palivo a platnosť dokladov.')}
        icon={<Car className="w-5 h-5" />}
        actions={
          <button
            type="button"
            onClick={() => {
              setEditing(null)
              setIsCreating(true)
            }}
            className={cn('flex items-center gap-1.5 px-3 py-2 rounded-lg', BUTTON_PRIMARY, 'text-xs')}
          >
            <Plus className="w-3.5 h-3.5" /> <T>Pridať vozidlo</T>
          </button>
        }
      />

      {(warnings.expired > 0 || warnings.soon > 0) && (
        <div
          className={cn(
            'flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2.5 rounded-xl border text-sm',
            warnings.expired > 0
              ? 'bg-red-500/10 border-red-500/20 text-red-600'
              : 'bg-amber-500/10 border-amber-500/20 text-amber-600',
          )}
          data-fleet-warnings={warnings.expired + warnings.soon}
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {warnings.expired > 0 && (
            <span className="font-semibold" data-fleet-expired={warnings.expired}>
              {warnings.expired} {tt('dokladov po platnosti')}
            </span>
          )}
          {warnings.expired > 0 && warnings.soon > 0 && <span className="opacity-40">·</span>}
          {warnings.soon > 0 && (
            <span data-fleet-soon={warnings.soon}>
              {warnings.soon} {tt('končí do 30 dní')}
            </span>
          )}
        </div>
      )}

      {/* FB-816, §9.537 - the failure comes before the loading branch and before the empty state. Drawn after
          either one it is unreachable on an early return and second-best on a ternary: the all-clear renders
          first and the manager reads it. `isLoading` is untouched, because it was already right. */}
      {isError ? (
        <VehicleFleetLoadFailed retry={() => void refetch()} />
      ) : isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {[1, 2, 3].map((placeholder) => (
            <div
              key={placeholder}
              className="h-64 rounded-2xl bg-muted/40 border border-border animate-pulse"
            />
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-12">
          <EmptyState
            icon={<Car className="w-10 h-10" />}
            title={tt('Žiadne vozidlá')}
            description={tt('Pridajte prvé vozidlo.')}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {sorted.map((vehicle) => (
            <VehicleCard
              key={vehicle.id}
              vehicle={vehicle}
              now={now}
              onEdit={() => {
                setIsCreating(false)
                setEditing(vehicle)
              }}
              onDelete={() => setPendingDelete(vehicle)}
            />
          ))}
        </div>
      )}

      {(isCreating || editing) && (
        <VehicleModal
          vehicle={editing}
          onClose={() => {
            setEditing(null)
            setIsCreating(false)
          }}
          onSave={save}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          open
          title={tt('Odstrániť vozidlo')}
          description={`${tt('Naozaj odstrániť')} „${pendingDelete.name}“?`}
          confirmLabel={tt('Odstrániť')}
          variant="danger"
          onClose={() => setPendingDelete(null)}
          onConfirm={() => {
            const target = pendingDelete
            setPendingDelete(null)
            deleteVehicle.mutate(
              { vehicleId: target.id ?? 0 },
              {
                onSuccess: refresh,
                // §9.385 - a vehicle a driver is signed on to is refused by name.
                onError: (caught) =>
                  toast({
                    variant: 'destructive',
                    title: tt('Odstránenie zlyhalo'),
                    description: apiErrorMessage(caught, tt('Server odstránenie odmietol.')),
                  }),
              },
            )
          }}
        />
      )}
    </div>
  )
}

function VehicleCard({
  vehicle,
  now,
  onEdit,
  onDelete,
}: {
  vehicle: Vehicle
  now: number
  onEdit: () => void
  onDelete: () => void
}) {
  const litres = litresUsed(vehicle)
  const costCents = fuelCostCents(vehicle)

  return (
    <div
      className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-4 hover:shadow-md transition-shadow"
      data-vehicle={vehicle.licensePlate}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Car className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-foreground truncate">{vehicle.name}</h3>
            <span className="text-sm font-mono text-muted-foreground">{vehicle.licensePlate}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {vehicle.active === false && (
            <StatusBadge color="zinc" label={tt('Neaktívne')} />
          )}
          <button
            type="button"
            aria-label={tt('Upraviť')}
            onClick={onEdit}
            className={cn(BUTTON_GHOST, 'p-1.5 rounded-lg hover:bg-accent')}
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            type="button"
            aria-label={tt('Odstrániť')}
            onClick={onDelete}
            className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-500 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-muted/30 px-3 py-2.5 space-y-1.5">
        <MetricRow
          label={tt('Začiatočný stav')}
          value={`${KM_FORMAT.format(vehicle.odometer ?? 0)} km`}
        />
        <MetricRow
          label={tt('Celkové km z rozvozov')}
          value={`${KM_FORMAT.format(vehicle.totalDeliveryKm ?? 0)} km`}
          tone="text-emerald-600"
        />
        <MetricRow
          label={tt('Spotreba paliva')}
          value={`${formatDecimal(litres, 1)} l`}
          icon={<Fuel className="w-3.5 h-3.5" />}
        />
        {costCents > 0 && (
          <MetricRow label={tt('Náklady na palivo')} value={formatCents(costCents)} />
        )}
        <div className="border-t border-border pt-1.5 flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-sm text-muted-foreground font-medium">
            <Gauge className="w-4 h-4" /> <T>Konečný stav</T>
          </span>
          <span className="text-sm font-bold text-foreground tabular-nums">
            {KM_FORMAT.format(odometerReading(vehicle))} km
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <Fuel className="w-4 h-4 flex-shrink-0" />
          <span className="font-semibold text-foreground tabular-nums">
            {formatDecimal(Number(vehicle.fuelConsumption ?? 0), 1)}
          </span>
          <span>l/100km</span>
        </span>
        <span className="h-3 w-px bg-border" />
        <span
          className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold',
            FUEL_TYPE_CLASSES[vehicle.fuelType ?? 'benzin'] ?? FUEL_TYPE_CLASSES.benzin,
          )}
        >
          <Fuel className="w-3 h-3" />
          {FUEL_TYPE_LABELS[vehicle.fuelType ?? 'benzin'] ?? vehicle.fuelType}
        </span>
        <span className="h-3 w-px bg-border" />
        <TireBadge type={vehicle.tireType} />
      </div>

      {/* Two by two, not four across: four badges on a card that is already two to a row would set
          each at about forty pixels, and a date is not readable at that width. */}
      <div className="grid grid-cols-2 gap-2">
        <ExpiryBadge label="STK" date={vehicle.stkExpiry} now={now} />
        <ExpiryBadge label="EK" date={vehicle.ekExpiry} now={now} />
        <ExpiryBadge label={tt('Poistka')} date={vehicle.insuranceExpiry} now={now} />
        <ExpiryBadge label={tt('Známka')} date={vehicle.vignetteExpiry} now={now} />
      </div>
    </div>
  )
}

function MetricRow({
  label,
  value,
  tone,
  icon,
}: {
  label: string
  value: string
  tone?: string
  icon?: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between text-xs text-muted-foreground">
      <span className="flex items-center gap-1">
        {icon} {label}
      </span>
      <span className={cn('tabular-nums font-medium', tone ?? 'text-foreground')}>{value}</span>
    </div>
  )
}

function ExpiryBadge({
  label,
  date,
  now,
}: {
  label: string
  date: string | null | undefined
  now: number
}) {
  const status = expiryStatus(date, now)
  return (
    <div
      className={cn('flex flex-col gap-0.5 px-2.5 py-1.5 rounded-lg border text-xs', EXPIRY_CLASSES[status])}
      data-expiry={label}
      data-expiry-status={status}
    >
      <span className="font-medium opacity-70">{label}</span>
      <span className="font-semibold tabular-nums">{date ? formatDayMonth(date) : '-'}</span>
    </div>
  )
}

function TireBadge({ type }: { type: string | undefined }) {
  if (type === 'winter') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-600 border border-blue-500/20 text-xs font-semibold">
        <Snowflake className="w-3 h-3" /> <T>Zimné</T>
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-600 border border-orange-500/20 text-xs font-semibold">
      <Sun className="w-3 h-3" /> <T>Letné</T>
    </span>
  )
}
